import {
  LEGACY_MEDICAL_CONDITION_IDS,
  MEDICAL_CONDITION_OTHER_ID,
  MEDICAL_CONDITIONS_API_OPTIONS,
  MEDICAL_CONDITIONS_OPTIONS,
} from '@/constants/medical-conditions';
import type { Client } from '@/types/client';
import { formatDate } from '@/utils/date-utils';
import { formatDisplayLabel } from '@/utils/display-labels';

export function isActiveMedicalConditionStatus(status: unknown): boolean {
  return String(status ?? '').trim().toLowerCase() === 'active';
}

export function dedupeMedicalConditionsByName<T extends { name: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = item.name.trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function getMedicalConditionStatusColor(status: string): string {
  switch (status?.toLowerCase()) {
    case 'active':
      return '#EF4444';
    case 'managed':
      return '#14B8A6';
    case 'resolved':
      return '#64748B';
    default:
      return '#14B8A6';
  }
}

export type FormattedMedicalConditionDetail = {
  name: string;
  autoimmuneSummary?: string;
  diagnosedOn: string;
  statusLabel: string;
  statusColor: string;
  metrics: Array<{ label: string; value: string; isAlert?: boolean }>;
};

/** Active conditions only, one entry per display name. */
export function formatActiveMedicalConditionsFromApi(conditions: unknown[]): FormattedMedicalConditionDetail[] {
  const list = Array.isArray(conditions) ? conditions : [];

  const formatted = list
    .filter((condition) => isActiveMedicalConditionStatus((condition as { status?: unknown })?.status))
    .map((condition) => {
      const record = condition as Record<string, unknown>;
      const name = formatDisplayLabel(record.displayName ?? record.conditionName ?? record.name ?? '');
      const diagnosisDate = record.diagnosisDate ?? record.diagnosedOn;

      return {
        name,
        autoimmuneSummary:
          typeof record.autoimmuneSummary === 'string' ? record.autoimmuneSummary : undefined,
        diagnosedOn: diagnosisDate
          ? `Diagnosed ${formatDate(String(diagnosisDate), 'MMM yyyy', String(diagnosisDate))}`
          : '',
        statusLabel: record.status ? formatDisplayLabel(String(record.status)) : 'Unknown',
        statusColor: getMedicalConditionStatusColor(String(record.status ?? '')),
        metrics: Array.isArray(record.metrics)
          ? (record.metrics as Array<Record<string, unknown>>).map((metric) => ({
              label: formatDisplayLabel(metric.name ?? ''),
              value: `${metric.value ?? ''} ${metric.unit ?? ''}`.trim(),
              isAlert: Boolean(metric.isOutOfRange),
            }))
          : [],
      };
    })
    .filter((condition) => Boolean(condition.name));

  return dedupeMedicalConditionsByName(formatted);
}

export type MedicalConditionDisplayItem = {
  name: string;
  autoimmuneSummary?: string;
  status?: string;
};

type MedicalConditionSource = {
  displayName?: string;
  name?: string;
  conditionName?: string;
  status?: string;
  autoimmuneSummary?: string;
};

function mapMedicalConditionDisplays(conditions: MedicalConditionSource[]): MedicalConditionDisplayItem[] {
  return conditions
    .map((condition) => ({
      name: formatDisplayLabel(condition.displayName ?? condition.name ?? condition.conditionName ?? ''),
      autoimmuneSummary: condition.autoimmuneSummary,
      status: condition.status,
    }))
    .filter((condition) => Boolean(condition.name));
}

/** Active overview/API conditions only, one entry per display name. */
export function getActiveMedicalConditionDisplays(
  conditions: MedicalConditionSource[],
): MedicalConditionDisplayItem[] {
  const formatted = mapMedicalConditionDisplays(conditions).filter((condition) =>
    isActiveMedicalConditionStatus(condition.status),
  );

  return dedupeMedicalConditionsByName(formatted);
}

function getMedicalConditionStatusSortOrder(status?: string): number {
  const normalized = String(status ?? '').trim().toLowerCase();
  if (normalized === 'active') return 0;
  if (normalized === 'resolved') return 1;
  return 2;
}

export function sortMedicalConditionsByStatus<T extends { name: string; status?: string }>(items: T[]): T[] {
  return [...items].sort((left, right) => {
    const orderDiff =
      getMedicalConditionStatusSortOrder(left.status) - getMedicalConditionStatusSortOrder(right.status);
    if (orderDiff !== 0) return orderDiff;
    return left.name.localeCompare(right.name);
  });
}

/** All active and resolved conditions for overview; one entry per name (prefers active). */
export function getMedicalConditionDisplays(conditions: MedicalConditionSource[]): MedicalConditionDisplayItem[] {
  const formatted = mapMedicalConditionDisplays(conditions);
  const byName = new Map<string, MedicalConditionDisplayItem>();

  for (const item of formatted) {
    const key = item.name.trim().toLowerCase();
    const existing = byName.get(key);
    if (!existing) {
      byName.set(key, item);
      continue;
    }
    if (isActiveMedicalConditionStatus(item.status) && !isActiveMedicalConditionStatus(existing.status)) {
      byName.set(key, item);
    }
  }

  return sortMedicalConditionsByStatus(Array.from(byName.values()));
}

function isPredefinedMedicalConditionId(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return MEDICAL_CONDITIONS_API_OPTIONS.some((condition) => condition.id.toLowerCase() === normalized);
}

export function mapMedicalConditionToApiValue(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';

  const normalized = trimmed.toLowerCase();
  if (normalized === MEDICAL_CONDITION_OTHER_ID) return '';

  const legacyMapped = LEGACY_MEDICAL_CONDITION_IDS[normalized];
  if (legacyMapped) return legacyMapped;

  const matched = MEDICAL_CONDITIONS_API_OPTIONS.find(
    (condition) =>
      condition.id.toLowerCase() === normalized || condition.name.toLowerCase() === normalized,
  );
  if (matched) return matched.id;

  return trimmed;
}

export function resolveMedicalConditionLabel(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';

  const normalized = trimmed.toLowerCase();
  const legacyMapped = LEGACY_MEDICAL_CONDITION_IDS[normalized];
  const lookupValue = legacyMapped ?? trimmed;

  const matched = MEDICAL_CONDITIONS_API_OPTIONS.find(
    (condition) =>
      condition.id.toLowerCase() === lookupValue.toLowerCase() ||
      condition.name.toLowerCase() === lookupValue.toLowerCase(),
  );

  return matched?.name ?? formatDisplayLabel(trimmed);
}

function getMedicalConditionDedupeKey(value: string): string {
  const apiValue = mapMedicalConditionToApiValue(value);
  return isPredefinedMedicalConditionId(apiValue)
    ? apiValue.toLowerCase()
    : apiValue.trim().toLowerCase();
}

function dedupeMedicalConditionValues(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    const normalized = mapMedicalConditionToApiValue(value);
    const key = getMedicalConditionDedupeKey(normalized);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    result.push(normalized);
  }

  return result;
}

/** Active condition ids/names used for editable client profile fields. */
export function getEditableMedicalConditionValues(client: Client | null | undefined): string[] {
  if (!client) return [];

  if (Array.isArray(client.medicalConditionDetails) && client.medicalConditionDetails.length > 0) {
    const values = client.medicalConditionDetails
      .filter((condition) => isActiveMedicalConditionStatus(condition.status))
      .map((condition) => {
        const conditionId = String(condition.conditionId ?? '').trim();
        if (conditionId.toLowerCase() === 'other') {
          return String(condition.conditionName ?? '').trim();
        }
        return conditionId || String(condition.conditionName ?? '').trim();
      })
      .filter(Boolean);

    return dedupeMedicalConditionValues(values);
  }

  const fromLegacy = String(client.medicalConditions ?? '')
    .split(',')
    .map((item) => mapMedicalConditionToApiValue(item.trim()))
    .filter(Boolean);

  return dedupeMedicalConditionValues(fromLegacy);
}

export function mapMedicalConditionsToApiValues(values: string[]): string[] {
  return dedupeMedicalConditionValues(values.map((value) => mapMedicalConditionToApiValue(value)));
}

/** Display labels for medical conditions with status `active` only. */
export function getActiveMedicalConditionLabels(client: Client | null | undefined): string[] {
  if (!client) return [];

  if (Array.isArray(client.medicalConditionDetails)) {
    const labels = client.medicalConditionDetails
      .filter((condition) => isActiveMedicalConditionStatus(condition.status))
      .map((condition) => {
        const conditionId = String(condition.conditionId ?? '').trim();
        const conditionName = String(condition.conditionName ?? '').trim();
        if (conditionId.toLowerCase() === MEDICAL_CONDITION_OTHER_ID) {
          return formatDisplayLabel(conditionName);
        }
        return resolveMedicalConditionLabel(conditionId || conditionName);
      })
      .filter(Boolean);

    return dedupeMedicalConditionsByName(labels.map((name) => ({ name }))).map((item) => item.name);
  }

  const fromLegacy = String(client.medicalConditions ?? '')
    .split(',')
    .map((item) => resolveMedicalConditionLabel(item.trim()))
    .filter(Boolean);

  return dedupeMedicalConditionsByName(fromLegacy.map((name) => ({ name }))).map((item) => item.name);
}
